import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import { FaCalendarAlt, FaFileInvoiceDollar, FaEdit, FaClock, FaMapMarkerAlt, FaUser, FaPhone, FaBan, FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import BottomNav from "../common/BottomNav";
import InvoiceModal from "../modal/InvoiceModal";
import AppHeader from "../common/AppHeader";
import Spinner from '../common/Spinner';
import {
  getEventServicesBySpIdApi
} from "../../services/eventService";
import { getAvailabilityExceptions } from "../../services/availabilityService";
import { useTranslation } from "react-i18next";
import styles from "../css/BookingCalendar.module.css";

// Date formatting utility functions
const formatDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateTime = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatDateOnly = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'completed':
    case 'confirmed':
      return '#4caf50';
    case 'cancelled':
      return '#f44336';
    case 'rejected':
      return '#ff9800';
    case 'pending':
      return '#FFD600';
    case 'expired':
      return '#9e9e9e';
    default:
      return '#1976d2';
  }
};

// Custom Dropdown Component
function CustomDropdown({ value, onChange, options, placeholder, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <div
        className={`${styles.dropdownHeader} ${disabled ? styles.disabled : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={value ? styles.selectedText : styles.placeholderText}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown
          className={`${styles.dropdownIcon} ${isOpen ? styles.rotated : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className={styles.dropdownList}>
          {options.map((option, index) => (
            <div
              key={index}
              className={`${styles.dropdownItem} ${option.value === value ? styles.selected : ""} ${option.disabled ? styles.disabledItem : ""}`}
              onClick={() => {
                if (!option.disabled) {
                  onChange(option.value);
                  setIsOpen(false);
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BookingCalendar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [busyDates, setBusyDates] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [invoiceBooking, setInvoiceBooking] = useState(null);

  useEffect(() => {
    async function fetchBookingsAndBusyDates() {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user.spId || "";

        // Fetch event services
        const servicesRes = await getEventServicesBySpIdApi(userId);

        // For now, set empty bookings array (can be populated from other sources if needed)
        const allBookings = [];

        // Map and normalize booking data
        const normalizedBookings = allBookings
          .map(b => ({
            ...b,
            eventDate: b.eventDate || b.bookingDate || b.serviceDate,
            eventTime: b.eventTime || b.bookingTime,
            customerName: b.customerName || b.userName,
            customerPhone: b.customerPhone || b.userPhone || b.contactNumber,
            address: b.address || b.eventAddress || b.location,
            bookingStatus: b.bookingStatus || b.status,
            bookingId: b.bookingId || b.esbId,
            serviceName: b.serviceName || b.businessName,
            serviceId: b.serviceId || b.esId,
            type: 'booking'
          }))
          // Filter out cancelled, rejected, and expired bookings for calendar view
          .filter(b => {
            const status = b.bookingStatus?.toLowerCase();
            return status !== 'cancelled' && status !== 'rejected' && status !== 'expired';
          });

        setBookings(normalizedBookings);

        // Fetch busy dates for all event services
        const services = Array.isArray(servicesRes?.data) ? servicesRes.data : [];
        setServices(services);
        const allBusyDates = [];

        for (const service of services) {
          try {
            const response = await getAvailabilityExceptions('EVENT', service.esId);
            const exceptions = (response?.responseCode === 200 && response.responseData) 
              ? response.responseData 
              : [];
            
            exceptions.forEach(exception => {
              allBusyDates.push({
                ...exception,
                serviceName: service.businessName,
                serviceId: service.esId,
                type: 'busy',
                unavailableFrom: exception.unavailableFrom,
                unavailableTo: exception.unavailableTo,
                reason: exception.reason
              });
            });
          } catch (error) {
            console.error(`Error fetching busy dates for service ${service.esId}:`, error);
          }
        }

        setBusyDates(allBusyDates);
      } catch (error) {
        console.error("Error fetching bookings and busy dates:", error);
        setBookings([]);
        setBusyDates([]);
      }
      setLoading(false);
    }
    fetchBookingsAndBusyDates();
  }, []);

  // Filter bookings and busy dates by selected service
  const filteredBookings = selectedServiceFilter
    ? bookings.filter(b => {
        try {
          const selectedService = JSON.parse(selectedServiceFilter);
          return b.serviceId === selectedService.esId || b.serviceName === selectedService.businessName;
        } catch (e) {
          return false;
        }
      })
    : []; // Show nothing until a service is selected

  const filteredBusyDates = selectedServiceFilter
    ? busyDates.filter(bd => {
        try {
          const selectedService = JSON.parse(selectedServiceFilter);
          return bd.serviceId === selectedService.esId;
        } catch (e) {
          return false;
        }
      })
    : []; // Show nothing until a service is selected

  const bookingDates = filteredBookings.map(b => formatDate(b.eventDate));
  const busyDateStrings = filteredBusyDates.map(bd => formatDate(bd.unavailableFrom));

  const selectedBookings = filteredBookings.filter(
    b => formatDate(b.eventDate) === formatDate(selectedDate)
  );

  const selectedBusyDates = filteredBusyDates.filter(
    bd => formatDate(bd.unavailableFrom) === formatDate(selectedDate)
  );

  const allSelectedItems = [...selectedBookings, ...selectedBusyDates];

  const upcomingBookings = filteredBookings
    .filter(b => new Date(formatDateOnly(b.eventDate)) > new Date(formatDateOnly(selectedDate)))
    .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));

  const upcomingGroups = {};
  upcomingBookings.forEach(b => {
    const dateStr = formatDateOnly(b.eventDate);
    if (!upcomingGroups[dateStr]) upcomingGroups[dateStr] = [];
    upcomingGroups[dateStr].push(b);
  });

  function tileClassName({ date, view }) {
    if (view === 'month') {
      const d = formatDate(date);
      const today = formatDate(new Date());

      const hasBooking = bookingDates.includes(d);
      const hasBusy = busyDateStrings.includes(d);

      if (hasBooking && hasBusy) {
        // Both booking and busy mark on same date
        return 'booking-date-mixed';
      } else if (hasBusy) {
        // Only busy mark
        return 'busy-date';
      } else if (hasBooking) {
        if (d < today) {
          return 'booking-date booking-date-old';
        }
        return 'booking-date';
      }
    }
    return null;
  }

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // Service filter options
  const serviceFilterOptions = [
    ...(services.length === 0
      ? []
      : services.map((service) => ({
          value: JSON.stringify(service),
          label: service.businessName || service.displayName,
        }))),
  ];

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <AppHeader 
          title={t("BookingCalendar.booking_calendar", "Booking Calendar")} 
          showBack={true}
          rightIcon={<FaEdit size={20} />}
          onRightIconClick={() => navigate('/mark-busy')}
        />
        <div className={styles.loadingContainer}>
          <Spinner text={t("BookingCalendar.loading", "Loading...")} />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <AppHeader 
        title={t("BookingCalendar.booking_calendar", "Booking Calendar")} 
        showBack={true}
        rightIcon={<FaEdit size={20} />}
        onRightIconClick={() => navigate('/mark-busy')}
      />

      <div className={styles.contentWrapper}>
        {/* Service Filter */}
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>
            {t("BookingCalendar.select_service", "Select a Service")}
          </label>
          <CustomDropdown
            value={selectedServiceFilter}
            onChange={setSelectedServiceFilter}
            options={serviceFilterOptions}
            placeholder={t("BookingCalendar.choose_service", "Choose a service to view bookings...")}
          />
        </div>

        <div className={styles.calendarSection}>
          <div className={styles.calendarTitle}>
            <FaCalendarAlt color="#232b35" size={16} />
            <span>{selectedServiceFilter ? t("BookingCalendar.select_date", "Select a date to view bookings") : t("BookingCalendar.select_service_first", "Select a service to view its calendar")}</span>
          </div>
          <div className={styles.calendarWrapper}>
            <Calendar
              value={selectedDate}
              onChange={handleDateClick}
              tileClassName={tileClassName}
            />
          </div>
          {/* Calendar Legend */}
          <div className={styles.calendarLegend}>
            <div className={styles.legendItem}>
              <div className={styles.legendColor} style={{ background: '#1976d2' }}></div>
              <span>{t("BookingCalendar.legend_booking", "Booking")}</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendColor} style={{ background: '#ffe082' }}></div>
              <span>{t("BookingCalendar.legend_busy", "Marked Busy")}</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendColor} style={{ background: 'linear-gradient(135deg, #1976d2 0%, #1976d2 50%, #f59e0b 50%, #f59e0b 100%)' }}></div>
              <span>{t("BookingCalendar.legend_both", "Both")}</span>
            </div>
          </div>
        </div>

        <div className={styles.calendarSection}>
          <div className={styles.sectionTitle}>
            <span>
              {t("BookingCalendar.bookings_on_date", { date: formatDate(selectedDate) }, `Bookings on ${formatDate(selectedDate)}`)}
            </span>
          </div>
          {allSelectedItems.length === 0 ? (
            <div className={styles.noBookingsCenter}>
              {t("BookingCalendar.no_bookings_found", "No bookings or busy dates found.")}
            </div>
          ) : (
            allSelectedItems.map((item, index) => (
              item.type === 'booking' ? (
                <div key={`booking-${item.bookingId}`} className={styles.bookingCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.bookingId}>
                      {t("BookingCalendar.booking_id", { id: item.bookingId }, `Booking #${item.bookingId}`)}
                    </div>
                    <button
                      className={styles.invoiceButton}
                      title={t("BookingCalendar.view_invoice", "View Invoice")}
                      onClick={() => setInvoiceBooking(item)}
                    >
                      <span className={styles.invoiceButtonText}>
                        {t("BookingCalendar.view_invoice", "Invoice")}
                      </span>
                      <FaFileInvoiceDollar size={12} color="#1976d2" />
                    </button>
                  </div>
                  <div className={styles.cardContent}>
                    {item.serviceName && (
                      <div className={styles.cabText}>
                        <FaCalendarAlt size={14} color="#667eea" /> <span className={styles.cabName}>{item.serviceName}</span>
                      </div>
                    )}
                    {item.customerName && (
                      <div className={styles.detailText}>
                        <FaUser size={12} color="#64748b" /> {t("BookingCalendar.customer", "Customer")}: {item.customerName}
                      </div>
                    )}
                    {item.customerPhone && (
                      <div className={styles.detailText}>
                        <FaPhone size={12} color="#64748b" /> {item.customerPhone}
                      </div>
                    )}
                    <div className={styles.detailText}>
                      <FaClock size={12} color="#64748b" /> {t("BookingCalendar.event_date", "Event Date")}: {formatDateTime(item.eventDate)}
                    </div>
                    {item.eventTime && (
                      <div className={styles.detailText}>
                        {t("BookingCalendar.event_time", "Time")}: {item.eventTime}
                      </div>
                    )}
                    {item.address && (
                      <div className={styles.locationText}>
                        <FaMapMarkerAlt size={12} color="#64748b" /> {item.address}
                      </div>
                    )}
                    <div className={styles.statusContainer}>
                      <span
                        className={styles.statusBadge}
                        style={{ background: getStatusColor(item.bookingStatus) }}
                      >
                        {t(`BookingCalendar.status.${item.bookingStatus?.toLowerCase()}`, item.bookingStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={`busy-${item.exceptionId}-${index}`} className={styles.bookingCard} style={{ borderLeft: '4px solid #f59e0b' }}>
                  <div className={styles.cardHeader}>
                    <div className={styles.bookingId} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaBan size={14} color="#f59e0b" />
                      {t("BookingCalendar.marked_busy", "Marked Busy")}
                    </div>
                  </div>
                  <div className={styles.cardContent}>
                    {item.serviceName && (
                      <div className={styles.cabText}>
                        <FaCalendarAlt size={14} color="#667eea" /> <span className={styles.cabName}>{item.serviceName}</span>
                      </div>
                    )}
                    <div className={styles.detailText}>
                      <FaClock size={12} color="#64748b" /> {t("BookingCalendar.date", "Date")}: {formatDate(item.unavailableFrom)}
                    </div>
                    <div className={styles.detailText}>
                      <FaBan size={12} color="#64748b" /> {t("BookingCalendar.reason", "Reason")}: {item.reason}
                    </div>
                    <div className={styles.statusContainer}>
                      <span
                        className={styles.statusBadge}
                        style={{ background: '#f59e0b' }}
                      >
                        {t("BookingCalendar.unavailable", "Unavailable")}
                      </span>
                    </div>
                  </div>
                </div>
              )
            ))
          )}
        </div>

        {Object.keys(upcomingGroups).length > 0 && (
          <>
            {Object.keys(upcomingGroups).map(dateStr => (
              <React.Fragment key={dateStr}>
                <div className={styles.dateLabel}>
                  <span className={styles.dateLabelText}>
                    {dateStr}
                  </span>
                </div>
                {upcomingGroups[dateStr].map(b => (
                  <div key={b.bookingId} className={styles.bookingCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.bookingId}>
                        {t("BookingCalendar.booking_id", { id: b.bookingId }, `Booking #${b.bookingId}`)}
                      </div>
                      <button
                        className={styles.invoiceButton}
                        title={t("BookingCalendar.view_invoice", "View Invoice")}
                        onClick={() => setInvoiceBooking(b)}
                      >
                        <span className={styles.invoiceButtonText}>
                          {t("BookingCalendar.view_invoice", "Invoice")}
                        </span>
                        <FaFileInvoiceDollar size={12} color="#1976d2" />
                      </button>
                    </div>
                    <div className={styles.cardContent}>
                      {b.serviceName && (
                        <div className={styles.cabText}>
                          <FaCalendarAlt size={14} color="#667eea" /> <span className={styles.cabName}>{b.serviceName}</span>
                        </div>
                      )}
                      {b.customerName && (
                        <div className={styles.detailText}>
                          <FaUser size={12} color="#64748b" /> {t("BookingCalendar.customer", "Customer")}: {b.customerName}
                        </div>
                      )}
                      {b.customerPhone && (
                        <div className={styles.detailText}>
                          <FaPhone size={12} color="#64748b" /> {b.customerPhone}
                        </div>
                      )}
                      <div className={styles.detailText}>
                        <FaClock size={12} color="#64748b" /> {t("BookingCalendar.event_date", "Event Date")}: {formatDateTime(b.eventDate)}
                      </div>
                      {b.eventTime && (
                        <div className={styles.detailText}>
                          {t("BookingCalendar.event_time", "Time")}: {b.eventTime}
                        </div>
                      )}
                      {b.address && (
                        <div className={styles.locationText}>
                          <FaMapMarkerAlt size={12} color="#64748b" /> {b.address}
                        </div>
                      )}
                      <div className={styles.statusContainer}>
                        <span
                          className={styles.statusBadge}
                          style={{ background: getStatusColor(b.bookingStatus) }}
                        >
                          {t(`BookingCalendar.status.${b.bookingStatus?.toLowerCase()}`, b.bookingStatus)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      <BottomNav />

      {invoiceBooking && (
        <InvoiceModal
          booking={invoiceBooking}
          onClose={() => setInvoiceBooking(null)}
        />
      )}
    </div>
  );
}